/**
 * Address Normalization Utilities
 *
 * Functions for normalizing address text for comparison and matching.
 */

/**
 * Normalize text for comparison
 * - Converts to lowercase
 * - Removes accents/diacritics
 * - Removes periods
 * - Normalizes whitespace
 */
export const normalizeText = (text: string): string => {
  if (!text) return "";

  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove accents
    .replace(/\./g, "") // Remove periods
    .replace(/\s+/g, " ") // Normalize spaces
    .trim();
};

/**
 * Mexican state name normalization map
 * Maps common variations to official state names
 */
const STATE_NORMALIZATION_MAP: Record<string, string> = {
  "ciudad de mexico": "Ciudad de Mexico",
  cdmx: "Ciudad de Mexico",
  df: "Ciudad de Mexico",
  "distrito federal": "Ciudad de Mexico",
  "estado de mexico": "Estado de Mexico",
  edomex: "Estado de Mexico",
  "mexico": "Estado de Mexico",
  "nuevo leon": "Nuevo Leon",
  nl: "Nuevo Leon",
  jalisco: "Jalisco",
  jal: "Jalisco",
  "baja california": "Baja California",
  bc: "Baja California",
  "baja california norte": "Baja California",
  "baja california sur": "Baja California Sur",
  bcs: "Baja California Sur",
  "quintana roo": "Quintana Roo",
  qroo: "Quintana Roo",
  aguascalientes: "Aguascalientes",
  ags: "Aguascalientes",
  campeche: "Campeche",
  camp: "Campeche",
  chiapas: "Chiapas",
  chis: "Chiapas",
  chihuahua: "Chihuahua",
  chih: "Chihuahua",
  coahuila: "Coahuila de Zaragoza",
  "coahuila de zaragoza": "Coahuila de Zaragoza",
  coah: "Coahuila de Zaragoza",
  colima: "Colima",
  col: "Colima",
  durango: "Durango",
  dgo: "Durango",
  guanajuato: "Guanajuato",
  gto: "Guanajuato",
  guerrero: "Guerrero",
  gro: "Guerrero",
  hidalgo: "Hidalgo",
  hgo: "Hidalgo",
  michoacan: "Michoacan de Ocampo",
  "michoacan de ocampo": "Michoacan de Ocampo",
  mich: "Michoacan de Ocampo",
  morelos: "Morelos",
  mor: "Morelos",
  nayarit: "Nayarit",
  nay: "Nayarit",
  oaxaca: "Oaxaca",
  oax: "Oaxaca",
  puebla: "Puebla",
  pue: "Puebla",
  queretaro: "Queretaro",
  qro: "Queretaro",
  "san luis potosi": "San Luis Potosi",
  slp: "San Luis Potosi",
  sinaloa: "Sinaloa",
  sin: "Sinaloa",
  sonora: "Sonora",
  son: "Sonora",
  tabasco: "Tabasco",
  tab: "Tabasco",
  tamaulipas: "Tamaulipas",
  tamps: "Tamaulipas",
  tlaxcala: "Tlaxcala",
  tlax: "Tlaxcala",
  veracruz: "Veracruz de Ignacio de la Llave",
  "veracruz de ignacio de la llave": "Veracruz de Ignacio de la Llave",
  ver: "Veracruz de Ignacio de la Llave",
  yucatan: "Yucatan",
  yuc: "Yucatan",
  zacatecas: "Zacatecas",
  zac: "Zacatecas",
};

/**
 * Normalize Mexican state name to official format
 * @param state - State name to normalize
 * @returns Normalized state name
 */
export const normalizeStateName = (state: string): string => {
  if (!state) return "";

  const normalized = normalizeText(state);
  return STATE_NORMALIZATION_MAP[normalized] || state;
};

/**
 * Convert text to uppercase without accents
 * Used for form field normalization
 */
export const toUpperCaseWithoutAccents = (text: string): string => {
  if (!text) return "";

  return text
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};
